"use client";

import React, { useState, ChangeEvent, useRef, useEffect } from "react";
import Image from "next/image";
import LoadingIcon from "@/assets/icons/loading-icon";
import Link from "next/link";
import { useQuery } from "@tanstack/react-query";
import { productService } from "@/services/product";
import { useDebounce } from "@/hook/use-debounce";
import { Typography } from "../typography";

const SearchBar: React.FC = () => {
  const [query, setQuery] = useState<string>("");
  const divRef = useRef<HTMLDivElement>(null);

  const searchTerm = useDebounce(query, 300);

  const { data, isLoading } = useQuery(["search-products", searchTerm], () => {
    if (searchTerm) {
      return productService.getAllProducts({
        page: 1,
        limit: 10,
        name: searchTerm?.trim(),
      });
    }

    return undefined;
  });

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value?.trimStart());
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (divRef.current && !divRef.current.contains(event.target as Node)) {
      setQuery("");
    }
  };

  useEffect(() => {
    document.addEventListener("click", handleClickOutside, true);
    return () => {
      document.removeEventListener("click", handleClickOutside, true);
    };
  }, []);

  return (
    <div className="inline-block w-full" ref={divRef}>
      <div className="relative w-full">
        <input
          type="text"
          placeholder="Search for anything..."
          className="transition-all flex py-3 px-4 text-base border pr-10 rounded w-full focus-visible:ring-primary focus-visible:ring-2 focus:outline-none"
          value={query}
          onChange={handleInputChange}
        />
        <button className="bg-primary px-3 absolute top-0 rounded-tr rounded-br bottom-0 right-0 w-fit h-full">
          <Image
            src="/img/search-white.svg"
            alt="Search"
            className="w-5 h-5 m-auto"
            height={25}
            width={25}
          />
        </button>

        {query && (
          <div className="absolute mt-1 w-full bg-white shadow-lg rounded-b">
            {isLoading && (
              <div className="w-full py-2 flex justify-center">
                <LoadingIcon size={30} />
              </div>
            )}

            {!isLoading && data?.data.data.length === 0 && (
              <p className="p-2">No results found</p>
            )}
            {!isLoading && data?.data.data.length !== 0 && (
              <div className="max-h-[300px] border p-2 overflow-auto">
                {data?.data.data.map((product, i) => (
                  <>
                    <Link
                      key={product?.id}
                      href={`/products/${product?.slug}`}
                      className="block p-2 cursor-pointer"
                      onClick={() => setQuery("")}
                    >
                      <div className="flex items-center">
                        <Image
                          src={product?.images?.[0]}
                          alt={product.slug}
                          height={40}
                          width={40}
                          className="h-fit"
                        />
                        <div className="px-2">
                          <Typography tag="h4" className="line-clamp-1">
                            {product?.name}
                          </Typography>
                          <Typography tag="span" className="line-clamp-1">
                            {product?.description}
                          </Typography>
                        </div>
                      </div>
                    </Link>
                    {i !== (data?.data.data.length || 0) - 1 && <hr />}
                  </>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchBar;
